import * as d3 from 'd3';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SessionDataCompiled, SessionDataEntry } from '../types/api';

interface SessionDataVisualizerProps {
  sessionData: SessionDataCompiled;
  width?: number;
  height?: number;
}

export const SessionDataVisualizer: React.FC<SessionDataVisualizerProps> = ({
  sessionData,
  width  = 800,
  height = 500,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<SVGSVGElement>(null);

  // useRef to store the Score value
  const ScoreRef = useRef<any>({});
  const [data, setData] = useState<Record<string, { timestamp: number; duration: number; value: number }[]> | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [activeChannels, setActiveChannels] = useState({
    HR:      true,
    HRV:     true,
    'LF/HF': true,
    Fog:     true,
  });

  const toggleChannel = (channel: string) => {
    setActiveChannels(prev => ({
      ...prev,
      [channel]: !prev[channel as keyof typeof prev]
    }));
  };

  // Transform SessionDataCompiled into d3 compatible arrays
  useEffect(() => {
    const processSessionData = () => {
      const channelData = {
        HR:      [],
        HRV:     [],
        'LF/HF': [],
        Fog:     [],
      } as Record<string, { timestamp: number; duration: number; value: number }[]>;

      // Iterate sessionData to convert to channelData, unwinding in-line arrays as needed.
      let lastFog = 0;
      let validLFN = false;
      sessionData.data.forEach((entry: SessionDataEntry) => {
        if (entry.HR !== undefined && entry.HR !== 0) {
          if ( Array.isArray(entry.HR) ) {
            // Average the HR values (if an array)
            const averageHR = entry.HR.reduce((sum, hr) => sum + hr, 0) / entry.HR.length;
            channelData.HR.push({ timestamp: entry._t, duration: entry._d, value: averageHR })
          } else {
            channelData.HR.push({ timestamp: entry._t, duration: entry._d, value: entry.HR })
          }
        }
        if (entry.HRV !== undefined && entry.HRV !== 0) {
          if ( Array.isArray(entry.HRV) ) {
            // Average the HRV values (if an array)
            const averageHRV = entry.HRV.reduce((sum, hrv) => sum + hrv, 0) / entry.HRV.length;
            channelData.HRV.push({ timestamp: entry._t, duration: entry._d, value: averageHRV })
          } else {
            channelData.HRV.push({ timestamp: entry._t, duration: entry._d, value: entry.HRV })
          }
        }
        if (entry.Fog !== undefined) {
          let fogValue = 0
          if ( Array.isArray(entry.Fog) ) {
            // Average the Fog values (if an array)
            fogValue = entry.Fog.reduce((sum, fog) => sum + fog, 0) / entry.Fog.length;
          } else {
            fogValue = entry.Fog;
          }
          // Convert from 0-1 (Unity style) to percentage
          lastFog = fogValue * 100;
          channelData.Fog.push({ timestamp: entry._t, duration: entry._d, value: fogValue * 100 })
        } else if ( entry.HRV !== undefined && entry.HRV > 0 ) {
          // Fog values are only suppled when updated, so a persistent value will needs to be interpolated.
          channelData.Fog.push({ timestamp: entry._t, duration: entry._d, value: lastFog })
        }
        if (entry.LFN !== undefined) {
          let lfnValue = 0
          if ( Array.isArray(entry.LFN) ) {
            lfnValue = entry.LFN.reduce((sum, lfn) => sum + lfn, 0) / entry.LFN.length;
          } else {
            lfnValue = entry.LFN;
          }
          // Jump over leading artifactually high LFN values (which will also need to be addressed on the headset)
          if ( !validLFN ) {
            if ( lfnValue < 97 ) {
              validLFN = true;
            } else {
              return;
            }
          }
          // Convert from LFnu back to LF/HF ratio
          channelData['LF/HF'].push({ timestamp: entry._t, duration: entry._d, value: lfnValue > 0 ? lfnValue / (100 - lfnValue) : 0 })
        }
        if (entry.Score !== undefined) {
          ScoreRef.current = entry.Score;
        }
      });
      return channelData;
    };

    setData(processSessionData());
  }, [sessionData.data]);

  // Measure container width for responsive chart sizing
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const windowWidth = window.innerWidth;
        const chartContainerWidth = chartContainerRef.current.clientWidth;
        const boundingRect = chartContainerRef.current.getBoundingClientRect();
        const padding = boundingRect.left + ( document.documentElement.scrollWidth - boundingRect.right );
        const availableWidth = Math.max(windowWidth, chartContainerWidth) - padding;
        setContainerWidth(availableWidth);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(updateWidth);
    window.addEventListener('resize', updateWidth);

    // Also update when data changes (container might resize)
    if (data) {
      updateWidth();
    }

    return () => window.removeEventListener('resize', updateWidth);
  }, [data]);

  // Calculate value extents from data (available in both useEffect and JSX)
  const valueExtents = useMemo(() => {
    if (!data) return null;
    return {
      HR:      d3.extent(data.HR,       d => d.value) as [number, number],
      HRV:     d3.extent(data.HRV,      d => d.value) as [number, number],
      'LF/HF': d3.extent(data['LF/HF'], d => d.value) as [number, number],
      Fog:     [0, 100] as [number, number], // Fixed as percentage
    };
  }, [data]);

  useEffect(() => {
    if (
      !svgRef.current
      || !sessionData.data
      || sessionData.data.length === 0
      || !data
    ) {
      return;
    }

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    // Dimensions
    const margin = { top: 20, right: 160, bottom: 50, left: 40 };

    // Fixed minimum width for the chart (allows contraction)
    const fixedMinWidth = width; // Minimum chart width in pixels

    // Use container width if available, otherwise use default width
    // containerWidth is the width of the chart container div (already accounts for its own padding)
    const availableWidth = containerWidth > 0 ? containerWidth : fixedMinWidth;

    // Chart should be at least the fixed minimum, but expand/contract with container
    // However, if data requires more space, ensure we have that minimum
    const dynamicWidth = Math.max(availableWidth, fixedMinWidth);
    const chartWidth   = dynamicWidth - margin.left - margin.right;
    const chartHeight  = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr('width',  dynamicWidth)
      .attr('height', chartHeight   + margin.top  + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Time extent (same for all channels)
    // There's got to be a cheaper way of doing this than unspooling all the data into a common array. :(
    const timeExtent = d3.extent([
        ...data.HR,
        ...data.HRV,
        ...data['LF/HF'],
        ...data.Fog
      ],
      d => d.timestamp
    ) as Iterable<number>;

    // X Scale (seconds since start)
    const xScale = d3.scaleLinear()
      .domain(timeExtent)
      .range([0, chartWidth]);

    if (!valueExtents) return;

    // Y Scales (one per channel)
    const yScales = {
      HR: d3.scaleLinear()
        .domain([ valueExtents.HR[0] - 5, valueExtents.HR[1] + 5])
        .range([chartHeight, 0]),
      HRV: d3.scaleLinear()
        .domain([ valueExtents.HRV[0] - 5, valueExtents.HRV[1] + 5])
        .range([chartHeight, 0]),
      'LF/HF': d3.scaleLinear()
        .domain([ 0, valueExtents['LF/HF'][1] + 1 ])
        .range([chartHeight, 0]),
      Fog: d3.scaleLinear()
        .domain([0, 100]) // Fixed as a percentage, so hard coding this is fine.
        .range([chartHeight, 0])
    };

    // Colors for each channel
    const channelColors = {
      HR:      '#e74c3c', // red
      HRV:     '#2ecc71', // green
      'LF/HF': '#3498db', // blue
      Fog:     '#95a5a6', // gray
    };

    // Draw Fog channel first (as shaded area)
    const fogArea = d3.area<{ timestamp: number; value: number }>()
      .x(d => xScale(d.timestamp))
      .y0(chartHeight)
      .y1(d => yScales.Fog(d.value))
      .curve( d3.curveMonotoneX);

    svg.append('path')
      .datum(data.Fog)
      .attr('fill', channelColors.Fog)
      .attr('fill-opacity', activeChannels.Fog ? 0.2 : 0.05)
      .attr('d', fogArea);

    const fogLine = d3.line<{ timestamp: number; value: number }>()
      .x(d => xScale(d.timestamp))
      .y(d => yScales.Fog(d.value))
      .curve(d3.curveMonotoneX);

    svg.append('path')
      .datum(data.Fog)
      .attr('fill', 'none')
      .attr('stroke', channelColors.Fog)
      .attr('stroke-width', 1.5)
      .attr('opacity', activeChannels.Fog ? 0.5 : 0.15)
      .attr('d', fogLine);

    // Line generators for other channels
    const channels = ['HR', 'HRV', 'LF/HF'];
    channels.forEach(channel => {
      const line = d3.line<{ timestamp: number; value: number }>()
        .x(d => xScale(d.timestamp))
        .y(d => yScales[channel as keyof typeof yScales](d.value))
        .curve(d3.curveMonotoneX);

      svg.append('path')
        .datum(data[channel])
        .attr('fill', 'none')
        .attr('stroke', channelColors[channel as keyof typeof channelColors])
        .attr('stroke-width', 2)
        .attr('opacity', activeChannels[channel as keyof typeof activeChannels] ? 1 : 0.15)
        .attr('d', line);
    });

    // X Axis
    const xAxis = d3.axisBottom(xScale)
      .ticks(10);

    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis);

    // X-axis label
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + 40)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Time (seconds)');

    // Y Axes for each channel
    const yAxisHRV = d3.axisLeft(yScales.HRV).ticks(8);
    svg.append('g')
      .call(yAxisHRV)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -30)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .style('fill', channelColors.HRV)
      .style('font-size', '12px')
      .text('HRV (ms)');

    // TODO: make this axis match the channel color
    const yAxisHR = d3.axisRight(yScales.HR).ticks(8);
    svg.append('g')
      .attr('transform', `translate(${chartWidth}, 0)`)
      .call(yAxisHR)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 35)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .style('fill', channelColors.HR)
      .style('font-size', '12px')
      .text('HR (bpm)');

    const yAxisLFHF = d3.axisRight(yScales['LF/HF']).ticks(8);
    svg.append('g')
      .attr('transform', `translate(${chartWidth + 45}, 0)`)
      .call(yAxisLFHF)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 35)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .style('fill', channelColors['LF/HF'])
      .style('font-size', '12px')
      .text('LF/HF');

    // Grid lines
    svg.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .call(d3.axisLeft(yScales.HR)
        .ticks(10)
        .tickSize(-chartWidth)
        .tickFormat(null)
      );

    // Legend
    const legend = svg.append('g')
      .attr('transform', `translate(${chartWidth + 65}, ${chartHeight - 30})`);

    channels.forEach((channel, i) => {
      const legendRow = legend.append('g')
        .attr('transform', `translate(0, ${i * 15})`);

      legendRow.append('line')
        .attr('x1', 0)
        .attr('x2', 30)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', channelColors[channel as keyof typeof channelColors])
        .attr('stroke-width', 2);

      legendRow.append('text')
        .attr('x', 35)
        .attr('y', 5)
        .text(channel)
        .style('font-size', '12px')
        .style('fill', channelColors[channel as keyof typeof channelColors]);
    });

    // Tooltip
    const tooltip = d3.select('body').append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // Vertical cursor line
    const cursorLine = svg.append('line')
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4')
      .style('opacity', 0);

    // Hover overlay
    svg.append('rect')
      .attr('width', chartWidth)
      .attr('height', chartHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousemove', function (event) {
        const [mouseX] = d3.pointer(event);
        const x0 = xScale.invert(mouseX);

        // Show cursor line
        cursorLine
          .attr('x1', mouseX)
          .attr('x2', mouseX)
          .style('opacity', 1);

        const minutes = Math.floor(x0 / 60);
        const seconds = x0 % 60;
        let tooltipContent = `<strong>Time: ${minutes}m ${seconds.toFixed(0)}s</strong><br/>`;

        const allChannels = ['HR', 'HRV', 'LF/HF', 'Fog'];

        allChannels.forEach(channel => {
          const bisect = d3.bisector<{ timestamp: number; value: number }, number>(d => d.timestamp).left;
          const idx = bisect(data[channel], x0);
          if (idx < data[channel].length) {
            const d = data[channel][idx];
            tooltipContent += `<span style="color:${channelColors[channel as keyof typeof channelColors]}">${channel}: ${ channel === 'LF/HF' ? d.value.toFixed(1) : d.value.toFixed(0)}</span><br/>`;
          }
        });

        tooltip
          .html(tooltipContent)
          .style('left', (event.pageX + 15) + 'px')
          .style('top', (event.pageY - 28) + 'px')
          .style('opacity', 1);
      })
      .on('mouseout', () => {
        tooltip.style('opacity', 0);
        cursorLine.style('opacity', 0);
      });

    // Cleanup
    return () => {
      tooltip.remove();
    };
  }, [data, activeChannels, valueExtents, containerWidth, sessionData.data, width, height]);

  // Render pie chart for evaluation score
  const Score = ScoreRef.current;

  useEffect(() => {
    if (!pieChartRef.current || Score?.Evaluation === undefined) {
      return;
    }

    // Clear previous render
    d3.select(pieChartRef.current).selectAll('*').remove();

    const width = 200;
    const height = 100;
    const radius = width / 2 - 10;

    const svg = d3.select(pieChartRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height})`);

    // Create data for 4 segments (one for each evaluation level)
    const pieData = [
      { label: 'Sympathetic NS',   value: Score.TimeInZone.SympatheticNS,   index: 0 },
      { label: 'Developing',       value: Score.TimeInZone.Developing,      index: 1 },
      { label: 'Regulated',        value: Score.TimeInZone.Regulated,       index: 2 },
      { label: 'Highly Regulated', value: Score.TimeInZone.HighlyRegulated, index: 3 },
    ];

    // Colors for each segment
    const colors = ['#ef4444', '#f59e0b', '#3bf682', '#af73ef']; // red, amber, green, purple

    const pie = d3.pie<typeof pieData[0]>()
      .value(d => d.value)
      .sort(null)
      .startAngle(-Math.PI / 2) // Start at top
      .endAngle(Math.PI / 2); // End at bottom (semicircle)

    const arc = d3.arc<d3.PieArcDatum<typeof pieData[0]>>()
      .innerRadius(radius * 0.25)
      .outerRadius(radius);

    const arcs = svg.selectAll('.arc')
      .data(pie(pieData))
      .enter()
      .append('g')
      .attr('class', 'arc');

    arcs.append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => colors[i])
      .attr('opacity', 0.3)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    // Add labels
    /*
    arcs.append('text')
      .attr('transform', d => `translate(${arc.centroid(d)})`)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .attr('font-weight', 'normal')
      .text((d, i) => {
        const labels = ['SNS', 'Dev', 'Reg', 'High'];
        return labels[i];
      });
      */

    // Cleanup
    const thePie = pieChartRef.current;
    return () => {
      d3.select(thePie).selectAll('*').remove();
    };
  }, [Score?.Evaluation, sessionData.data, Score?.TimeInZone]);

  // Show error notice if no data is available
  if (!sessionData.data || sessionData.data.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-2 text-gray-800">
          Biometrics and Telemetry
        </h1>
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="font-semibold text-red-900 mb-1">No Data Available</h3>
              <p className="text-sm text-red-800">
                This session does not contain any data points. Please ensure the session has been properly recorded and contains measurement data.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const evaluationDecode = [
    "Sympathetic NS Dominant",
    "Developing",
    "Regulated",
    "Highly Regulated",
  ];
  const hrvZoneDecode = [
    "Low",
    "Medium",
    "High",
  ];
  const lfhfZoneDecode = [
    "Sympathetic NS",
    "Balanced",
    "Optimal",
  ];
  const achivements = [];
  let   zoneTotals = 0;
  if ( Score.TimeInZone !== undefined ) {
    zoneTotals = Object.values(
        Score.TimeInZone as Record<string, number>
      ).reduce((sum: number, value: number) => sum + value, 0);
  }
  if ( Score.Achievements !== undefined ) {
    // Iterate Achievements keys
    for ( const key in Score.Achievements ) {
      if ( Score.Achievements[key] === true ) {
        achivements.push(key.replace(/([a-z])([A-Z])/g, '$1 $2'));
      }
    }
  }
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 text-gray-800">
        Biometrics and Telemetry
      </h1>
      <p className="text-gray-600 mb-6">
        Real-time visualization of HR, HRV, LF/HF, and Fog channels with different data ranges
      </p>
      <div ref={chartContainerRef} className="overflow-x-auto w-full">
        <svg ref={svgRef} className="block"></svg>
      </div>
      <div className="mt-6 flex flex-col md:flex-row gap-6">
        <div className="p-4 bg-blue-50 rounded-lg flex-1">
          <h3 className="font-semibold text-blue-900 mb-2">Channel Information (click to toggle):</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li
              onClick={() => toggleChannel('HR')}
              className="cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors"
              style={{ opacity: activeChannels.HR ? 1 : 0.3 }}
            >
              <span className="font-semibold" style={{color: '#e74c3c'}}>HR:</span> Heart Rate {valueExtents?.HR && valueExtents.HR[0] && valueExtents.HR[1] ? `(${valueExtents.HR[0].toFixed(0)}-${valueExtents.HR[1].toFixed(0)} bpm)` : '(bpm)'}
            </li>
            <li
              onClick={() => toggleChannel('HRV')}
              className="cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors"
              style={{ opacity: activeChannels.HRV ? 1 : 0.3 }}
            >
              <span className="font-semibold" style={{color: '#2ecc71'}}>HRV:</span> Heart Rate Variability {valueExtents?.HRV && valueExtents.HRV[0] && valueExtents.HRV[1] ? `(${valueExtents.HRV[0].toFixed(0)}-${valueExtents.HRV[1].toFixed(0)} ms)` : '(ms)'}
            </li>
            <li
              onClick={() => toggleChannel('LF/HF')}
              className="cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors"
              style={{ opacity: activeChannels['LF/HF'] ? 1 : 0.3 }}
            >
              <span className="font-semibold" style={{color: '#3498db'}}>LF/HF:</span> Low Frequency/High Frequency Ratio {valueExtents?.['LF/HF'] && valueExtents['LF/HF'][0] && valueExtents['LF/HF'][1] ? `(${valueExtents['LF/HF'][0].toFixed(2)}-${valueExtents['LF/HF'][1].toFixed(2)})` : ''}
            </li>
            <li
              onClick={() => toggleChannel('Fog')}
              className="cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors"
              style={{ opacity: activeChannels.Fog ? 1 : 0.3 }}
            >
              <span className="font-semibold" style={{color: '#95a5a6'}}>Fog:</span> Fog Percentage (0-100)
            </li>
          </ul>
        </div>
        {Score.Evaluation !== undefined && (
          <>
            <div className="p-4 bg-blue-50 rounded-lg flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">Score: {evaluationDecode[Score.Evaluation]} ({((Score.TotalPoints/Score.PossiblePoints)*100).toFixed(0)}%)</h3>
              <p className="text-sm text-blue-800">Average HRV: {Score.HRVAverage.toFixed(0)} ms ({hrvZoneDecode[Score.HRVAverageZone]})</p>
              <p className="text-sm text-blue-800">Average LF/HF: {Score.LFHFAverage.toFixed(2)} ({lfhfZoneDecode[Score.LFHFAverageZone]})</p>
              <p className="text-sm text-blue-800">Recovery Count: {Score.LFHFRecoveryCount}</p>
              {achivements.length > 0 && (
                <>
                  <p className="text-sm text-blue-800">Achievements: </p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {achivements.map((achievement) => (
                      <li key={achievement} className="list-disc list-inside">{achievement}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="p-4 bg-blue-50 rounded-lg flex-shrink-0">
              <h3 className="font-semibold text-blue-900 mb-2 text-center">Time Per Zone</h3>
              <svg ref={pieChartRef} className="block mx-auto"></svg>
              <p className="text-sm text-blue-800">Sympathetic NS: <span style={{ color: '#ef4444' }}>{(Score.TimeInZone.SympatheticNS/zoneTotals*100).toFixed(0)}%</span></p>
              <p className="text-sm text-blue-800">Developing: <span style={{ color: '#f59e0b' }}>{(Score.TimeInZone.Developing/zoneTotals*100).toFixed(0)}%</span></p>
              <p className="text-sm text-blue-800">Regulated: <span style={{ color: '#3ba682' }}>{(Score.TimeInZone.Regulated/zoneTotals*100).toFixed(0)}%</span></p>
              <p className="text-sm text-blue-800">Highly Regulated: <span style={{ color: '#8f73ef' }}>{(Score.TimeInZone.HighlyRegulated/zoneTotals*100).toFixed(0)}%</span></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
